import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminPagination from '../admin/AdminPagination';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import { useAdminTablePagination } from '../../hooks/useAdminTablePagination';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { fetchFinancialHistory, recuPdfApiPath } from '../../services/academiaApprenantService';
import { downloadAuthenticatedFile } from '../../services/api';
import { fetchWalletTransactions } from '../../services/walletService';
import { formatMoney } from '../../utils/currency';
import { formatTransactionStatut } from '../../utils/inscriptionPresentation';

const FINANCES_PAGE_SIZE = 10;
const WALLET_MOVEMENTS_PAGE_SIZE = 10;

type TimelineRow = {
  id: string;
  type: 'RECU' | 'TRANSACTION' | 'FRAIS_INSCRIPTION_COMPTE' | 'WALLET';
  filiereLibelle?: string;
  cohorteLibelle?: string;
  montant?: number;
  statut?: string;
  modePaiement?: string;
  reference?: string;
  numeroRecu?: string;
  date?: string;
  libelleOperation?: string;
  paymentKind?: string;
};

type FinancialHistory = {
  summary?: {
    totalFacture: number;
    totalPaye: number;
    resteGlobal: number;
    inscriptionsCount?: number;
  };
  timeline?: TimelineRow[];
};

type Movement = {
  id: string;
  type: string;
  direction: string;
  amount: number;
  status?: string;
  reference?: string;
  createdAt: string;
};

type Props = {
  devise?: string;
};

function formatDateTime(iso: string | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatRecuStatut(statut: string | undefined) {
  switch (statut) {
    case 'PAYEE':
      return 'Payé';
    case 'PRETE':
      return 'Prêt';
    case 'EN_ATTENTE':
      return 'En attente';
    case 'ANNULEE':
      return 'Annulé';
    default:
      return statut ?? '—';
  }
}

function statutTone(
  statut: string | undefined,
  rowType: TimelineRow['type'],
): 'success' | 'warn' | 'danger' | 'neutral' {
  const s = String(statut ?? '').toUpperCase();
  const isRecu = rowType === 'RECU';
  if (isRecu) {
    if (s === 'PAYEE' || s === 'PRETE') return 'success';
    if (s === 'EN_ATTENTE') return 'warn';
    if (s === 'ANNULEE') return 'danger';
    return 'neutral';
  }
  if (s === 'VALIDEE') return 'success';
  if (s === 'EN_ATTENTE') return 'warn';
  if (s === 'ECHOUEE' || s === 'ANNULEE') return 'danger';
  return 'neutral';
}

function formatModePaiement(mode: string | undefined) {
  if (!mode) return null;
  return mode.replace(/_/g, ' ').toLowerCase();
}

function getOperationTitle(row: TimelineRow) {
  if (row.libelleOperation) return row.libelleOperation;
  if (row.type === 'FRAIS_INSCRIPTION_COMPTE') return "Frais d'inscription au compte";
  if (row.type === 'RECU') return 'Reçu de paiement';
  return 'Paiement';
}

function getOperationIcon(row: TimelineRow): {
  name: 'description' | 'payments' | 'school' | 'badge' | 'account_balance_wallet';
  className: string;
} {
  if (row.type === 'WALLET') {
    return { name: 'account_balance_wallet', className: 'apprenant-finances-op__icon--wallet' };
  }
  if (row.type === 'FRAIS_INSCRIPTION_COMPTE') {
    return { name: 'badge', className: 'apprenant-finances-op__icon--compte' };
  }
  if (row.paymentKind === 'SOUSCRIPTION_INSCRIPTION') {
    return { name: 'school', className: 'apprenant-finances-op__icon--souscription' };
  }
  if (row.type === 'RECU') {
    return { name: 'description', className: 'apprenant-finances-op__icon--recu' };
  }
  return { name: 'payments', className: 'apprenant-finances-op__icon--tx' };
}

function movementLabel(type: string) {
  const map: Record<string, string> = {
    REFERRAL_BONUS_CREDIT: 'Bonus parrainage',
    FORMATION_WALLET_CREDIT: 'Crédit académique',
    FORMATION_PAYMENT_DEBIT: 'Paiement formation',
    RECHARGE_CREDIT: 'Recharge',
    TRANSFER_CREDIT: 'Transfert reçu',
    TRANSFER_DEBIT: 'Transfert envoyé',
    WITHDRAWAL_DEBIT: 'Retrait',
    FORMATEUR_REMUNERATION_CREDIT: 'Rémunération formateur',
  };
  return map[type] ?? type;
}

function movementStatusLabel(status?: string) {
  if (status === 'PENDING') return 'En attente';
  if (status === 'FAILED') return 'Échoué';
  return 'Validé';
}

function movementStatusTone(status?: string): 'success' | 'warn' | 'danger' {
  if (status === 'PENDING') return 'warn';
  if (status === 'FAILED') return 'danger';
  return 'success';
}

export default function ApprenantWalletHistoriqueTab({ devise = 'CDF' }: Props) {
  const [data, setData] = useState<FinancialHistory | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [movementsPagination, setMovementsPagination] = useState({
    page: 1,
    limit: WALLET_MOVEMENTS_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingRecuId, setDownloadingRecuId] = useState<string | null>(null);

  const loadFinancial = useCallback(async () => {
    setData((await fetchFinancialHistory()) as FinancialHistory);
  }, []);

  const loadMovements = useCallback(async (page = 1) => {
    setMovementsLoading(true);
    try {
      const result = await fetchWalletTransactions({ page, limit: WALLET_MOVEMENTS_PAGE_SIZE });
      setMovements((result?.items ?? []) as Movement[]);
      const pagination = result?.pagination ?? {};
      setMovementsPagination({
        page: Number(pagination.page ?? page),
        limit: Number(pagination.limit ?? WALLET_MOVEMENTS_PAGE_SIZE),
        total: Number(pagination.total ?? 0),
        totalPages: Math.max(1, Number(pagination.totalPages ?? 1)),
      });
    } finally {
      setMovementsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await Promise.all([loadFinancial(), loadMovements(1)]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [loadFinancial, loadMovements]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const timeline = data?.timeline ?? [];
  const {
    paginatedItems: paginatedTimeline,
    currentPage,
    totalPages,
    onPageChange,
    summary: timelinePaginationSummary,
  } = useAdminTablePagination(timeline, FINANCES_PAGE_SIZE);

  const movementsPaginationSummary = useMemo(() => {
    if (movementsPagination.total === 0) return 'Aucun mouvement';
    const start = (movementsPagination.page - 1) * movementsPagination.limit + 1;
    const end = Math.min(movementsPagination.page * movementsPagination.limit, movementsPagination.total);
    return `Affichage ${start}–${end} sur ${movementsPagination.total}`;
  }, [movementsPagination]);

  const downloadRecu = async (recuId: string) => {
    setDownloadingRecuId(recuId);
    try {
      await downloadAuthenticatedFile(recuPdfApiPath(recuId), `recu-${recuId.slice(0, 8)}.pdf`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Téléchargement du reçu impossible.');
    } finally {
      setDownloadingRecuId(null);
    }
  };

  const summary = data?.summary;

  if (loading) {
    return <p className="py-10 text-center text-sm text-on-surface-variant">Chargement de l&apos;historique…</p>;
  }

  return (
    <>
      {error ? (
        <div className="apprenant-finances-alert apprenant-finances-alert--error" role="alert">
          {error}
          <button type="button" className="apprenant-finances-alert__retry" onClick={() => void load()}>
            Réessayer
          </button>
        </div>
      ) : null}

      <div className="apprenant-finances">
        <section className="apprenant-finances-kpi" aria-label="Synthèse financière">
          <article className="apprenant-finances-kpi__card">
            <span className="apprenant-finances-kpi__icon apprenant-finances-kpi__icon--primary">
              <LucideSymbolIcon name="receipt_long" tone="inherit" size={20} />
            </span>
            <div>
              <p className="apprenant-finances-kpi__label">Total facturé</p>
              <p className="apprenant-finances-kpi__value apprenant-finances-kpi__value--sm">
                {formatMoney(summary?.totalFacture ?? 0, 'CDF')}
              </p>
            </div>
          </article>
          <article className="apprenant-finances-kpi__card">
            <span className="apprenant-finances-kpi__icon apprenant-finances-kpi__icon--emerald">
              <LucideSymbolIcon name="check_circle" tone="inherit" size={20} />
            </span>
            <div>
              <p className="apprenant-finances-kpi__label">Total payé</p>
              <p className="apprenant-finances-kpi__value apprenant-finances-kpi__value--sm">
                {formatMoney(summary?.totalPaye ?? 0, 'CDF')}
              </p>
            </div>
          </article>
          <article className="apprenant-finances-kpi__card">
            <span className="apprenant-finances-kpi__icon apprenant-finances-kpi__icon--amber">
              <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={20} />
            </span>
            <div>
              <p className="apprenant-finances-kpi__label">Reste à payer</p>
              <p className="apprenant-finances-kpi__value apprenant-finances-kpi__value--sm">
                {formatMoney(summary?.resteGlobal ?? 0, 'CDF')}
              </p>
              {summary?.inscriptionsCount != null ? (
                <p className="apprenant-finances-kpi__hint">
                  {summary.inscriptionsCount} inscription{summary.inscriptionsCount !== 1 ? 's' : ''}
                </p>
              ) : null}
            </div>
          </article>
        </section>

        <section className="apprenant-finances-panel" aria-labelledby="apprenant-wallet-historique-ops-heading">
          <header className="apprenant-finances-panel__header">
            <span className="apprenant-finances-panel__header-icon" aria-hidden>
              <LucideSymbolIcon name="history" tone="inherit" size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="apprenant-wallet-historique-ops-heading" className="apprenant-finances-panel__title">
                Paiements & reçus
              </h2>
              <p className="apprenant-finances-panel__subtitle">
                {timeline.length} opération{timeline.length !== 1 ? 's' : ''} · inscriptions et formations
              </p>
            </div>
            <Link to="/app/formations" className="apprenant-finances-panel__header-link">
              Formations
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
            </Link>
          </header>
          <div className="apprenant-finances-panel__body">
            {timeline.length === 0 ? (
              <div className="apprenant-finances-empty">
                <LucideSymbolIcon name="payments" tone="primary" size={40} className="opacity-80" />
                <p>Aucune opération enregistrée.</p>
                <p className="text-xs">Vos paiements et reçus apparaîtront ici après inscription.</p>
                <Link to="/app/formations" className="apprenant-finances-empty__cta">
                  <LucideSymbolIcon name="school" tone="inherit" size={18} className="text-white" />
                  Parcourir les formations
                </Link>
              </div>
            ) : (
              <>
                <ul className="apprenant-finances-ops">
                  {paginatedTimeline.map((row) => {
                    const isRecu = row.type === 'RECU';
                    const isCompte = row.type === 'FRAIS_INSCRIPTION_COMPTE';
                    const isWallet = row.type === 'WALLET';
                    const id = String(row.id);
                    const statutLabel = isRecu
                      ? formatRecuStatut(row.statut)
                      : formatTransactionStatut(row.statut ?? '');
                    const tone = statutTone(row.statut, row.type);
                    const modeLabel = !isRecu ? formatModePaiement(row.modePaiement) : null;
                    const icon = getOperationIcon(row);
                    const operationTitle = getOperationTitle(row);

                    return (
                      <li key={`${row.type}-${id}`} className="apprenant-finances-op">
                        <span className={`apprenant-finances-op__icon ${icon.className}`} aria-hidden>
                          <LucideSymbolIcon name={icon.name} tone="inherit" size={20} />
                        </span>
                        <div className="apprenant-finances-op__main min-w-0 flex-1">
                          <div className="apprenant-finances-op__head">
                            <p className="apprenant-finances-op__type">{operationTitle}</p>
                            <span
                              className={`apprenant-finances-op__badge apprenant-finances-op__badge--${tone}`}
                            >
                              {statutLabel}
                            </span>
                          </div>
                          <p className="apprenant-finances-op__formation">
                            {isWallet ? (
                              <span className="font-normal text-on-surface-variant">Portefeuille numérique</span>
                            ) : isCompte ? (
                              <span className="font-normal text-on-surface-variant">
                                Ouverture du compte apprenant
                              </span>
                            ) : (
                              <>
                                {row.filiereLibelle ?? '—'}
                                {row.cohorteLibelle ? (
                                  <>
                                    <span aria-hidden> · </span>
                                    <span className="font-normal text-on-surface-variant">
                                      {row.cohorteLibelle}
                                    </span>
                                  </>
                                ) : null}
                              </>
                            )}
                          </p>
                          <p className="apprenant-finances-op__meta">
                            <span>{formatDateTime(row.date)}</span>
                            {isRecu && row.numeroRecu ? (
                              <>
                                <span aria-hidden> · </span>
                                <span className="font-mono">{row.numeroRecu}</span>
                              </>
                            ) : null}
                            {!isRecu && modeLabel ? (
                              <>
                                <span aria-hidden> · </span>
                                <span className="capitalize">{modeLabel}</span>
                              </>
                            ) : null}
                            {!isRecu && row.reference ? (
                              <>
                                <span aria-hidden> · </span>
                                <span className="font-mono">{row.reference}</span>
                              </>
                            ) : null}
                          </p>
                        </div>
                        <div className="apprenant-finances-op__aside">
                          <p className="apprenant-finances-op__amount">
                            {formatMoney(Number(row.montant ?? 0), 'CDF')}
                          </p>
                          {isRecu ? (
                            <button
                              type="button"
                              disabled={downloadingRecuId === id}
                              onClick={() => void downloadRecu(id)}
                              className="apprenant-finances-op__action"
                            >
                              <LucideSymbolIcon name="download" tone="inherit" size={14} />
                              {downloadingRecuId === id ? '…' : 'PDF'}
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <AdminPagination
                  className="apprenant-finances-ops__pagination"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  summary={timelinePaginationSummary}
                />
              </>
            )}
          </div>
        </section>

        <section className="apprenant-finances-panel" aria-labelledby="apprenant-wallet-movements-heading">
          <header className="apprenant-finances-panel__header">
            <span className="apprenant-finances-panel__header-icon" aria-hidden>
              <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="apprenant-wallet-movements-heading" className="apprenant-finances-panel__title">
                Mouvements portefeuille
              </h2>
              <p className="apprenant-finances-panel__subtitle">
                {movementsPagination.total} mouvement{movementsPagination.total !== 1 ? 's' : ''} · crédits,
                retraits et transferts
              </p>
            </div>
          </header>
          <div className="apprenant-finances-panel__body">
            {movementsLoading && movements.length === 0 ? (
              <p className="py-6 text-center text-sm text-on-surface-variant">Chargement des mouvements…</p>
            ) : movements.length === 0 ? (
              <div className="apprenant-finances-empty">
                <LucideSymbolIcon name="account_balance_wallet" tone="primary" size={40} className="opacity-80" />
                <p>Aucun mouvement portefeuille.</p>
              </div>
            ) : (
              <>
                <ul className="apprenant-finances-ops">
                  {movements.map((m) => {
                    const isCredit = m.direction === 'CREDIT';
                    const statusTone = movementStatusTone(m.status);
                    return (
                      <li key={m.id} className="apprenant-finances-op">
                        <span
                          className={`apprenant-finances-op__icon ${
                            isCredit
                              ? 'apprenant-wallet-op__icon--credit'
                              : 'apprenant-wallet-op__icon--debit'
                          }`}
                          aria-hidden
                        >
                          <LucideSymbolIcon
                            name={isCredit ? 'add_circle' : 'payments'}
                            tone="inherit"
                            size={20}
                          />
                        </span>
                        <div className="apprenant-finances-op__main min-w-0 flex-1">
                          <div className="apprenant-finances-op__head">
                            <p className="apprenant-finances-op__type">{movementLabel(m.type)}</p>
                            <span
                              className={`apprenant-finances-op__badge apprenant-finances-op__badge--${statusTone}`}
                            >
                              {movementStatusLabel(m.status)}
                            </span>
                          </div>
                          <p className="apprenant-finances-op__meta">
                            <span>{formatDateTime(m.createdAt)}</span>
                            {m.reference ? (
                              <>
                                <span aria-hidden> · </span>
                                <span className="font-mono text-xs">{String(m.reference).slice(0, 24)}</span>
                              </>
                            ) : null}
                          </p>
                        </div>
                        <div className="apprenant-finances-op__aside">
                          <p
                            className={`apprenant-finances-op__amount ${isCredit ? 'apprenant-wallet-op__amount--credit' : 'apprenant-wallet-op__amount--debit'}`}
                          >
                            {isCredit ? '+' : '−'}
                            {formatMoney(m.amount, devise)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <AdminPagination
                  className="apprenant-finances-ops__pagination"
                  currentPage={movementsPagination.page}
                  totalPages={movementsPagination.totalPages}
                  onPageChange={(page) => void loadMovements(page)}
                  summary={movementsPaginationSummary}
                />
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
